/**
 * Sitewide entity JSON-LD — single source of truth.
 *
 * Convention used across the codebase:
 *   • SCHEMA_ORG_ID            → /#org             (Organization)
 *   • SCHEMA_LOCALBUSINESS_ID  → /#organization    (LocalBusiness — same legal entity, physical-location node)
 *   • SCHEMA_PERSON_ID         → /meet-chris#chris-heidlebaugh   (Founder/Author)
 *
 * These IDs are referenced from per-page schemas (Article.author, Service.provider,
 * Review.itemReviewed, etc.) so search engines can stitch the graph together
 * into one canonical entity. Changing an `@id` here without updating consumers
 * breaks Knowledge Graph linking — be careful.
 */

export const SITE_URL = "https://coloradowebimpressions.com";

export const SCHEMA_ORG_ID = `${SITE_URL}/#org`;
export const SCHEMA_LOCALBUSINESS_ID = `${SITE_URL}/#organization`;
export const SCHEMA_PERSON_ID = `${SITE_URL}/meet-chris#chris-heidlebaugh`;

/**
 * Canonical Google Maps place URL for the agency's verified GBP listing.
 * Embeds CID (0xc60ca26c53d71ee9) and Place ID (/g/11c0xqrpzr). Used in
 * both sameAs (Organization) and hasMap (LocalBusiness).
 */
const GOOGLE_MAPS_PLACE_URL =
  "https://www.google.com/maps/place/Colorado+Web+Impressions/@38.9359976,-104.7653118,845m/data=!3m2!1e3!4b1!4m6!3m5!1s0x87132ece8b07b43f:0xc60ca26c53d71ee9!8m2!3d38.9359976!4d-104.7653118!16s%2Fg%2F11c0xqrpzr";

const ORG_SAME_AS = [
  // Google Business Profile — highest-weight local entity reference
  "https://maps.app.goo.gl/HgGdpsS4vNubWWk19",
  // Canonical Google Maps place URL — embeds CID + Place ID
  GOOGLE_MAPS_PLACE_URL,
  // Owned social profiles
  "https://www.facebook.com/coloradowebimpressions/",
  "https://www.instagram.com/coloradowebimpressions/",
  "https://www.linkedin.com/company/colorado-web-impressions/",
  "https://www.youtube.com/channel/UCobHjvCeHGsOsHFDp9cAOlQ",
  "https://x.com/cwebimpressions",
  "https://open.spotify.com/show/1QEaGYsfCspVL17n2CCje4",
  // Review / accreditation entities
  "https://www.yelp.com/biz/colorado-web-impressions-colorado-springs",
  "https://www.bbb.org/us/co/colorado-springs/profile/web-design/colorado-web-impressions-0785-87368646",
  "https://trustanalytica.org/us/co/colorado-springs/reviews/colorado-web-impressions",
  // B2B agency directories
  "https://www.designrush.com/agency/profile/colorado-web-impressions",
  "https://clutch.co/profile/colorado-web-impressions",
  "https://www.topdevelopers.co/profile/coloradowebimpressions",
  "https://theorg.com/org/colorado-web-impressions",
  // Map / local-listing entities
  "https://www.mapquest.com/us/colorado/colorado-web-impressions-454269381",
  // Press / newsroom entities
  "https://markets.businessinsider.com/news/stocks/colorado-web-impressions-announces-ai-integration-to-revolutionize-digital-marketing-and-drive-client-success-1033819065",
  "https://www.prunderground.com/newsrooms/colorado-web-impressions",
  "https://www.einpresswire.com/newsroom/colorado_web_impressions/",
];

const PERSON_SAME_AS = [
  `${SITE_URL}/meet-chris`,
  `${SITE_URL}/about`,
  "https://chrisheidlebaugh.com",
  "https://digitalmarketingcoach.com",
  "https://www.linkedin.com/in/chrisheidlebaugh",
  "https://www.facebook.com/chrisheidlebaugh",
  "https://x.com/chrisheidlebaugh",
  "https://www.youtube.com/@chrisheidlebaugh",
  "https://www.instagram.com/chrisheidlebaugh",
  // Authority / professional entities
  "https://www.imdb.com/name/nm18250804/",
  "https://podcasts.apple.com/us/podcast/digital-marketing-coach-with-chris-heidlebaugh/id1875135728",
];

/**
 * Canonical NAP (Name, Address, Phone) — single source of truth.
 * Use these constants everywhere on the site so citations match exactly.
 * Inconsistent NAP weakens local SEO ranking.
 */
export const BUSINESS_NAME = "Colorado Web Impressions";
export const BUSINESS_PHONE_DISPLAY = "(719) 419-3935";
export const BUSINESS_PHONE_E164 = "+1-719-419-3935";
export const BUSINESS_PHONE_TEL = "tel:+17194193935";
export const BUSINESS_EMAIL = "info@coloradowebimpressions.com";
export const BUSINESS_STREET = "3210 E Woodmen Rd STE 110";
export const BUSINESS_LOCALITY = "Colorado Springs";
export const BUSINESS_REGION = "CO";
export const BUSINESS_POSTAL = "80920";
export const BUSINESS_COUNTRY = "US";
export const BUSINESS_ADDRESS_FULL = `${BUSINESS_STREET}, ${BUSINESS_LOCALITY}, ${BUSINESS_REGION} ${BUSINESS_POSTAL}`;

const HQ_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: BUSINESS_STREET,
  addressLocality: BUSINESS_LOCALITY,
  addressRegion: BUSINESS_REGION,
  postalCode: BUSINESS_POSTAL,
  addressCountry: BUSINESS_COUNTRY,
} as const;

/**
 * Colorado cities served — surfaced in LocalBusiness `areaServed` so Google
 * and AI engines understand the agency's full coverage area, not just HQ.
 * Coordinates are the canonical municipal centroids.
 */
const SERVICE_CITIES: ReadonlyArray<{ name: string; lat: number; lng: number }> = [
  { name: "Colorado Springs", lat: 38.8339, lng: -104.8214 },
  { name: "Denver", lat: 39.7392, lng: -104.9903 },
  { name: "Aurora", lat: 39.7294, lng: -104.8319 },
  { name: "Lakewood", lat: 39.7047, lng: -105.0814 },
  { name: "Centennial", lat: 39.5807, lng: -104.8772 },
  { name: "Littleton", lat: 39.6133, lng: -105.0166 },
  { name: "Highlands Ranch", lat: 39.5539, lng: -104.9689 },
  { name: "Castle Rock", lat: 39.3722, lng: -104.8561 },
  { name: "Parker", lat: 39.5186, lng: -104.7614 },
  { name: "Monument", lat: 39.0917, lng: -104.8728 },
  { name: "Pueblo", lat: 38.2544, lng: -104.6091 },
  { name: "Fort Collins", lat: 40.5853, lng: -105.0844 },
  { name: "Loveland", lat: 40.3978, lng: -105.0749 },
  { name: "Greeley", lat: 40.4233, lng: -104.7091 },
  { name: "Longmont", lat: 40.1672, lng: -105.1019 },
  { name: "Boulder", lat: 40.0150, lng: -105.2705 },
  { name: "Broomfield", lat: 39.9205, lng: -105.0867 },
  { name: "Thornton", lat: 39.8680, lng: -104.9719 },
  { name: "Westminster", lat: 39.8367, lng: -105.0372 },
  { name: "Arvada", lat: 39.8028, lng: -105.0875 },
  { name: "Grand Junction", lat: 39.0639, lng: -108.5506 },
  { name: "Durango", lat: 37.2753, lng: -107.8801 },
  { name: "Glenwood Springs", lat: 39.5505, lng: -107.3248 },
  { name: "Steamboat Springs", lat: 40.4850, lng: -106.8317 },
  { name: "Vail", lat: 39.6403, lng: -106.3742 },
  { name: "Frisco", lat: 39.5742, lng: -106.0978 },
  { name: "Cañon City", lat: 38.4417, lng: -105.2422 },
  { name: "Woodland Park", lat: 38.9939, lng: -105.0569 },
  { name: "Salida", lat: 38.5347, lng: -105.9989 },
  { name: "Montrose", lat: 38.4783, lng: -107.8762 },
];
/**
 * First-class service offerings — each has a dedicated hub page on the site.
 * `name` matches the on-page H1 (minus tagline suffixes) so the schema entity
 * and the visible heading are unambiguously the same service.
 *
 * Used in two places:
 *  1. `LOCAL_BUSINESS_SCHEMA.hasOfferCatalog` — each Offer references the
 *     Service by `@id` here instead of inlining a stub.
 *  2. `buildOfferingServiceJsonLd()` — emits a standalone Service node per
 *     offering, injected sitewide via `__root.tsx`. Each hub page also
 *     emits its own (richer) Service with the same `@id`, so search engines
 *     entity-merge them instead of seeing duplicates.
 */
export const SERVICE_OFFERINGS = [
  {
    name: "Colorado Web Design",
    slug: "/web-design",
    serviceType: "Web Design",
    description:
      "Custom lead-generating websites built for Colorado businesses — fast, mobile-first, and engineered to convert search traffic into calls and form fills.",
  },
  {
    name: "Colorado Local SEO",
    slug: "/local-seo",
    serviceType: "Local Search Engine Optimization",
    description:
      "City-by-city local SEO across Colorado — Map Pack rankings, Google Business Profile optimization, and local citations that put your business in front of buyers ready to call.",
  },
  {
    name: "Colorado Google Maps & GBP",
    slug: "/google-maps",
    serviceType: "Google Business Profile Optimization",
    description:
      "Google Business Profile and Google Maps optimization for Colorado service businesses — 3-pack rankings, photo and post strategy, review velocity, and Q&A management.",
  },
  {
    name: "Colorado Google Ads & PPC",
    slug: "/google-ads-ppc",
    serviceType: "Pay-Per-Click Advertising",
    description:
      "Google Ads, Local Services Ads, and PPC management for Colorado businesses — tight geo-targeting, conversion tracking, and weekly optimization for measurable lead cost.",
  },
  {
    name: "GEO & AI Search Optimization",
    slug: "/geo-ai",
    serviceType: "Generative Engine Optimization",
    description:
      "Generative Engine Optimization for ChatGPT, Perplexity, Gemini, and Google AI Overviews — structured content, entity schema, and citation strategy so AI engines surface your business.",
  },
  {
    name: "Colorado Digital Marketing",
    slug: "/digital-marketing",
    serviceType: "Digital Marketing",
    description:
      "Full-stack digital marketing for Colorado businesses — strategy, SEO, paid media, content, and conversion optimization run by one owner-led Colorado Springs team.",
  },
] as const;

/**
 * Build a standalone Service JSON-LD node per offering. Returns an array
 * (one object per offering) ready to JSON.stringify into `<script>` tags.
 */
export function buildOfferingServiceJsonLd() {
  return SERVICE_OFFERINGS.map((s) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}${s.slug}#service`,
    name: s.name,
    description: s.description,
    url: `${SITE_URL}${s.slug}`,
    serviceType: s.serviceType,
    provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
    areaServed: [
      { "@type": "State", name: "Colorado" },
      { "@type": "AdministrativeArea", name: "Front Range" },
    ],
  }));
}


/** Organization entity — abstract company / brand node. */
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": SCHEMA_ORG_ID,
  name: "Colorado Web Impressions",
  alternateName: "CWI",
  legalName: "Colorado Web Impressions",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/logo.png`,
    width: 512,
    height: 512,
  },
  image: `${SITE_URL}/logo.png`,
  description:
    "Colorado-based web design, SEO, Google Maps, PPC, social media, and AI search agency. Serving Colorado businesses since 2006.",
  foundingDate: "2006",
  founder: { "@id": SCHEMA_PERSON_ID },
  employee: [{ "@id": SCHEMA_PERSON_ID }],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: BUSINESS_PHONE_E164,
      contactType: "customer service",
      email: BUSINESS_EMAIL,
      areaServed: "US-CO",
      availableLanguage: ["English"],
    },
  ],
  address: HQ_ADDRESS,
  areaServed: { "@type": "State", name: "Colorado" },
  knowsAbout: [
    "Search Engine Optimization",
    "Local SEO",
    "Web Design",
    "Web Development",
    "Google Business Profile Optimization",
    "Google Maps Optimization",
    "Pay-Per-Click Advertising",
    "Google Ads",
    "Social Media Marketing",
    "Generative Engine Optimization",
    "AI Search Optimization",
    "Conversion Rate Optimization",
  ],
  knowsLanguage: ["en-US"],
  slogan: "Real Colorado marketing for real Colorado businesses.",
  sameAs: ORG_SAME_AS,
};

/** LocalBusiness entity — physical-location node for local search. */
export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ProfessionalService"],
  "@id": SCHEMA_LOCALBUSINESS_ID,
  name: "Colorado Web Impressions",
  url: SITE_URL,
  telephone: BUSINESS_PHONE_E164,
  email: BUSINESS_EMAIL,
  parentOrganization: { "@id": SCHEMA_ORG_ID },
  founder: { "@id": SCHEMA_PERSON_ID },
  address: HQ_ADDRESS,
  // HQ coordinates (Colorado Springs, 3210 E Woodmen Rd) — required for
  // LocalBusiness rich results in Google's structured data testing tool.
  geo: {
    "@type": "GeoCoordinates",
    latitude: 38.9359976,
    longitude: -104.7653118,
  },
  // Explicit link to the verified Google Maps listing so Google's Knowledge
  // Graph can stitch the on-page entity to the GBP place node.
  hasMap: GOOGLE_MAPS_PLACE_URL,
  // Marketing/agency price band — gives Google's $-$$$$ price-range badge
  // a concrete value instead of falling back to "unknown".
  priceRange: "$$",
  // Standard agency hours — used by Google for the "Open now" indicator
  // in Map Pack results.
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "16:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "09:00",
      closes: "13:00",
    },
  ],
  areaServed: [
    { "@type": "State", name: "Colorado" },
    ...SERVICE_CITIES.map((c) => ({
      "@type": "City" as const,
      name: c.name,
      address: {
        "@type": "PostalAddress",
        addressLocality: c.name,
        addressRegion: "CO",
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: c.lat, longitude: c.lng },
    })),
  ],
  serviceArea: {
    "@type": "GeoCircle",
    geoMidpoint: { "@type": "GeoCoordinates", latitude: 38.9359976, longitude: -104.7653118 },
    geoRadius: 250000,
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Digital Marketing Services",
    itemListElement: [
      // First-class offerings — reference standalone Service @ids emitted
      // sitewide via buildOfferingServiceJsonLd() so crawlers stitch the
      // Offer to a fully-described Service node (with url, areaServed,
      // provider, description) instead of an inline stub.
      ...SERVICE_OFFERINGS.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@id": `${SITE_URL}${s.slug}#service`, name: s.name },
      })),
      // Supplementary offerings without a dedicated hub page — keep
      // name-only Service stubs so they still appear in the catalog.
      ...["Content Marketing", "Conversion Rate Optimization"].map((name) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name, areaServed: { "@type": "State", name: "Colorado" } },
      })),
    ],
  },
  foundingDate: "2006",
  sameAs: ORG_SAME_AS,
};

/**
 * Person entity for the founder — primary E-E-A-T signal.
 * Emitted on every page so AI engines and Google's Knowledge Graph can attribute
 * authorship and expertise consistently. The full credentials/press list lives
 * on /meet-chris (the canonical mainEntityOfPage for this Person).
 */
export const PERSON_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": SCHEMA_PERSON_ID,
  name: "Chris Heidlebaugh",
  givenName: "Chris",
  familyName: "Heidlebaugh",
  url: `${SITE_URL}/meet-chris`,
  mainEntityOfPage: `${SITE_URL}/meet-chris`,
  // Multiple verified images of the same person strengthen Knowledge Graph
  // entity recognition (Google, Bing, Perplexity, Gemini). All hosted on the
  // canonical domain so crawlers attribute them to this Person entity.
  image: [
    {
      "@type": "ImageObject",
      url: `${SITE_URL}/chris-heidlebaugh.png`,
      caption: "Chris Heidlebaugh, Founder of Colorado Web Impressions",
    },
    {
      "@type": "ImageObject",
      url: `${SITE_URL}/chris-photos/chris-blazer-headshot.webp`,
      caption: "Chris Heidlebaugh — professional headshot",
    },
    {
      "@type": "ImageObject",
      url: `${SITE_URL}/chris-photos/chris-suit-portrait.webp`,
      caption: "Chris Heidlebaugh, Digital Marketing Coach",
    },
    {
      "@type": "ImageObject",
      url: `${SITE_URL}/chris-photos/chris-suit-seated.webp`,
      caption: "Chris Heidlebaugh, Founder & CEO of Colorado Web Impressions",
    },
    {
      "@type": "ImageObject",
      url: `${SITE_URL}/chris-photos/chris-office-casual.webp`,
      caption: "Chris Heidlebaugh at the Colorado Web Impressions office",
    },
    {
      "@type": "ImageObject",
      url: `${SITE_URL}/chris-photos/chris-casual-thinking.webp`,
      caption: "Chris Heidlebaugh, Colorado Springs digital marketing strategist",
    },
    {
      "@type": "ImageObject",
      url: `${SITE_URL}/chris-photos/chris-suit-street.webp`,
      caption: "Chris Heidlebaugh in downtown Colorado Springs",
    },
  ],
  jobTitle: "Founder & Lead Strategist",
  hasOccupation: [
    {
      "@type": "Occupation",
      name: "Digital Marketing Coach",
      occupationalCategory: "13-1161 Market Research Analysts and Marketing Specialists",
      skills:
        "Local SEO, Google Business Profile, Generative Engine Optimization, web design strategy, small business marketing coaching",
    },
    {
      "@type": "Occupation",
      name: "Founder & Web Strategist",
      occupationalCategory: "11-2021 Marketing Managers",
      skills:
        "Website strategy, conversion optimization, technical SEO, content strategy",
    },
  ],
  worksFor: { "@id": SCHEMA_ORG_ID },
  brand: [
    { "@type": "Brand", name: "Colorado Web Impressions", url: SITE_URL },
    { "@type": "Brand", name: "Digital Marketing Coach", url: "https://digitalmarketingcoach.com" },
    { "@type": "Brand", name: "Chris Heidlebaugh", url: "https://chrisheidlebaugh.com" },
  ],
  knowsAbout: [
    "Local SEO",
    "Google Business Profile",
    "Web Design",
    "Generative Engine Optimization",
    "Pay-Per-Click Advertising",
    "Conversion Rate Optimization",
    "Small Business Marketing",
    "Colorado Small Business",
    "Content Strategy",
  ],
  knowsLanguage: ["en-US"],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Colorado Springs",
    addressRegion: "CO",
    addressCountry: "US",
  },
  description:
    "Best-selling author, entrepreneur, and digital marketing coach with 25+ years helping business owners and marketing teams take control of their online presence. Founder of Colorado Web Impressions.",
  // Verifiable credentials and recognitions — strengthens E-E-A-T and gives
  // Google's Knowledge Graph attestable signals about expertise.
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Certification",
      name: "Google Ads Certified",
      recognizedBy: { "@type": "Organization", name: "Google" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Certification",
      name: "Google Analytics 4 Certified",
      recognizedBy: { "@type": "Organization", name: "Google" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Award",
      name: "Best-Selling Author — Digital Marketing",
    },
  ],
  alumniOf: {
    "@type": "Organization",
    name: "Colorado Web Impressions",
    url: SITE_URL,
  },
  award: [
    "20+ years building Colorado small business marketing programs",
    "Author of multiple best-selling digital marketing books",
  ],
  // Press / news mentions — explicit Article references give Knowledge Graph
  // and AI engines verifiable third-party attestation of expertise. Visible
  // counterpart of this list lives on /authors/chris-heidlebaugh.
  subjectOf: [
    {
      "@type": "Article",
      url: "https://markets.businessinsider.com/news/stocks/colorado-web-impressions-announces-ai-integration-to-revolutionize-digital-marketing-and-drive-client-success-1033819065",
      headline:
        "Colorado Web Impressions Announces AI Integration to Revolutionize Digital Marketing",
      datePublished: "2024-09-04",
      dateModified: "2024-09-04",
      image: `${SITE_URL}/logo.png`,
      author: { "@id": SCHEMA_PERSON_ID },
      publisher: { "@type": "Organization", name: "Markets Insider" },
    },
    {
      "@type": "Article",
      url: "https://www.prunderground.com/newsrooms/colorado-web-impressions",
      headline: "Colorado Web Impressions Newsroom",
      datePublished: "2024-01-15",
      dateModified: "2024-01-15",
      image: `${SITE_URL}/logo.png`,
      author: { "@id": SCHEMA_PERSON_ID },
      publisher: { "@type": "Organization", name: "PR Underground" },
    },
    {
      "@type": "Article",
      url: "https://www.einpresswire.com/newsroom/colorado_web_impressions/",
      headline: "Colorado Web Impressions Newsroom",
      datePublished: "2024-01-15",
      dateModified: "2024-01-15",
      image: `${SITE_URL}/logo.png`,
      author: { "@id": SCHEMA_PERSON_ID },
      publisher: { "@type": "Organization", name: "EIN Presswire" },
    },
  ],
  sameAs: PERSON_SAME_AS,
};

/**
 * Author entity — used on /authors/chris-heidlebaugh as the canonical Person
 * page. Mirrors PERSON_SCHEMA but sets mainEntityOfPage to the author URL so
 * Google treats /authors/chris-heidlebaugh as the source of truth for the
 * Person entity (better than /meet-chris which is a sales/about page).
 */
export const AUTHOR_PAGE_PERSON_SCHEMA = {
  ...PERSON_SCHEMA,
  mainEntityOfPage: `${SITE_URL}/authors/chris-heidlebaugh`,
};

/**
 * Build a WebPage JSON-LD with `speakable` selectors so voice assistants
 * (Google Assistant, Alexa, Siri-via-search) can read aloud the most useful
 * portion of a page. Selectors target the H1 + first paragraph of the article
 * body — the quickest spoken summary.
 *
 * Pass the canonical URL of the page. The selectors are intentionally broad
 * so they work on every page template without per-page wiring.
 */
export function buildSpeakableSchema(pageUrl: string, headline?: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: pageUrl,
    ...(headline ? { name: headline } : {}),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "[data-speakable]", "main p:first-of-type"],
    },
    isPartOf: { "@id": SCHEMA_ORG_ID },
    publisher: { "@id": SCHEMA_ORG_ID },
  } as const;
}

/**
 * Per-metro LocalBusiness branch nodes — lightweight nodes that signal
 * active service coverage in Colorado metros where we take work but don't
 * have a physical address. Each links back to the HQ LocalBusiness via
 * `branchOf` and to the parent Organization so Google's Knowledge Graph
 * stitches them into one entity.
 */
const BRANCH_METROS: ReadonlyArray<{
  slug: string;
  name: string;
  lat: number;
  lng: number;
  url?: string;
}> = [
  { slug: "denver", name: "Denver", lat: 39.7392, lng: -104.9903, url: `${SITE_URL}/denver` },
  { slug: "pueblo", name: "Pueblo", lat: 38.2544, lng: -104.6091, url: `${SITE_URL}/pueblo` },
  { slug: "castle-rock", name: "Castle Rock", lat: 39.3722, lng: -104.8561, url: `${SITE_URL}/castle-rock` },
  { slug: "parker", name: "Parker", lat: 39.5186, lng: -104.7614, url: `${SITE_URL}/parker` },
  { slug: "monument", name: "Monument", lat: 39.0917, lng: -104.8728, url: `${SITE_URL}/monument` },
];

// Coverage is a Service, never an invented branch office.
export const BRANCH_LOCAL_BUSINESSES = BRANCH_METROS.map((m) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE_URL}/${m.slug}#coverage-service`,
  name: `Web design and digital marketing in ${m.name}`,
  provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
  ...(m.url ? { url: m.url } : {}),
  priceRange: "$$",
  areaServed: { "@type": "City", name: m.name, address: { "@type": "PostalAddress", addressLocality: m.name, addressRegion: "CO", addressCountry: "US" } },
  serviceArea: {
    "@type": "GeoCircle",
    geoMidpoint: { "@type": "GeoCoordinates", latitude: m.lat, longitude: m.lng },
    geoRadius: 40234,
  },
}));

/** Reuse the real HQ identity; city names describe coverage, not offices. */
export function buildLocalBusinessSchema(areaNames: readonly string[] = []) {
  return {
    ...LOCAL_BUSINESS_SCHEMA,
    ...(areaNames.length > 0 ? {
      areaServed: [
        { "@type": "State", name: "Colorado" },
        ...areaNames.map((name) => ({
          "@type": name.toLowerCase().includes("western slope") ? "Place" : "City",
          name,
          containedInPlace: { "@type": "State", name: "Colorado" },
        })),
      ],
    } : {}),
  };
}

